import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";

const HERO_IMG =
  "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function BulkCustomTshirtsNZ() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Bulk Custom T-Shirts NZ",
    serviceType: "Bulk custom t-shirt printing and design",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
    areaServed: { "@type": "Country", name: "New Zealand" },
  };

  return (
    <>
      <SEOHead
        title="Bulk Custom T-Shirts NZ | Volume Pricing"
        description="Bulk custom t-shirts in NZ for staff uniforms, schools, churches, events and resale. Volume pricing, NZ-wide delivery from our Auckland studio."
        path="/bulk-custom-tshirts-nz"
        image={HERO_IMG}
        jsonLd={jsonLd}
      />

      <SEOPageLayout
        heroEyebrow="Volume pricing · NZ-wide"
        heroH1="Bulk Custom T-Shirts in NZ — Better Pricing at Scale"
        heroSub="High-volume custom t-shirt orders for staff uniforms, school cohorts, churches, festivals and resale. Auckland-based, shipped NZ-wide."
        heroImage={HERO_IMG}
        heroImageAlt="Stacks of bulk custom t-shirts ready for delivery NZ"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Bulk Custom T-Shirts NZ" },
        ]}
      >
        <h2>The bigger the order, the better the price</h2>
        <p>
          When you're outfitting a 200-person staff team, a whole-school
          cultural festival, a 14-week church youth programme, or a market
          stall full of resale stock, every dollar per unit matters. Tribal
          Designz Co. produces <strong>bulk custom t-shirts NZ</strong>-wide
          with serious volume discounts that kick in fast — we'd rather
          partner with you on big orders than nickel-and-dime small ones.
        </p>
        <p>
          We're an Auckland-based design and manufacturing studio with the
          capacity to handle large runs without the corporate-supplier feel.
          You'll deal with one designer and one project lead from start to
          finish — no anonymous portal, no "your account manager will email
          you back next Tuesday."
        </p>

        <h2>Common bulk-order use cases</h2>
        <ul>
          <li>
            <strong>Staff uniforms.</strong> Branded staff tees and polos
            for cafes, retail, gyms, construction crews, hospitality groups.
          </li>
          <li>
            <strong>Schools.</strong> Year-level shirts, leavers' shirts,
            cultural-festival cohort tees, sports-day kits.
          </li>
          <li>
            <strong>Churches &amp; youth groups.</strong> Camp shirts,
            volunteer tees, leadership-team polos.
          </li>
          <li>
            <strong>Festivals &amp; events.</strong> Volunteer crew, security,
            entertainer and supporter merch.
          </li>
          <li>
            <strong>Resale / merch drops.</strong> Cultural brands, gym
            apparel, micro-streetwear lines.
          </li>
          <li>
            <strong>Corporate gifting.</strong> Conference giveaways, end-of-year
            staff appreciation packs, brand activations.
          </li>
        </ul>

        <h2>Print methods we offer</h2>
        <p>
          For most bulk t-shirt jobs we'll recommend one of two methods:
        </p>
        <ul>
          <li>
            <strong>DTF (Direct-to-Film).</strong> Best for full-colour
            artwork, complex designs, smaller bulk runs (50–500), and tight
            timelines. Soft hand-feel, excellent wash durability.{" "}
            <Link to="/fast-tshirt-printing-auckland">Fast t-shirt printing →</Link>
          </li>
          <li>
            <strong>Screen printing.</strong> Best for very large runs (300+
            units) of simpler 1–4 colour designs. Most economical at scale.
          </li>
        </ul>
        <p>
          For full-garment patterns or photographic prints (e.g. cultural
          panels, all-over designs), we sublimate — same method we use for{" "}
          <Link to="/custom-rugby-uniforms-nz">rugby uniforms</Link> and{" "}
          <Link to="/custom-basketball-jerseys-nz">basketball jerseys</Link>.
        </p>

        <h2>Garment options</h2>
        <p>
          We stock and source a wide range of t-shirt blanks to suit
          different budgets and feels: classic 180gsm cotton, premium 200gsm
          combed cotton, tri-blend, polyester performance, organic cotton,
          and heavier oversized streetwear cuts. We can also work with your
          chosen blank brand if you have brand-standards to meet (AS Colour,
          Stencil, Gildan, etc.).
        </p>

        <h2>Lead times for bulk orders</h2>
        <p>
          Standard bulk lead times depend on quantity:
        </p>
        <ul>
          <li>50–150 units: 1–2 weeks</li>
          <li>150–500 units: 2–3 weeks</li>
          <li>500–2,000 units: 3–4 weeks</li>
          <li>2,000+ units: by quote, plan 4–6 weeks</li>
        </ul>
        <p>
          We can split deliveries if you need stock to flow in
          batches — handy for festival builds and progressive merch drops.
        </p>

        <h2>Design support included</h2>
        <p>
          A bulk order isn't just printing — getting the artwork right is
          half the job. Our designers work with you on layout, colour
          balance, garment placement and proof rounds before anything goes
          to production. If you're starting from scratch, we'll quote a
          design package alongside the print quote so the whole project sits
          in one place.
        </p>

        <h2>Auckland HQ, NZ-wide delivery</h2>
        <p>
          We pack and ship NZ-wide every week — Auckland, Hamilton,
          Tauranga, Wellington, Christchurch, Dunedin and everywhere in
          between. Bulk orders ship on pallets or as multi-box courier
          consignments depending on volume. Auckland clients can also
          collect directly from the studio.
        </p>

        <h2>Get a bulk quote</h2>
        <p>
          Send through your rough quantity, garment type, art status (do you
          have a design or do you need one?) and deadline. We'll come back
          within 24 hours with an itemised quote and lead time.
        </p>

        <p>
          New to ordering apparel in bulk?{" "}
          <Link to="/blog/sublimation-vs-dtf-printing">
            Read our guide on sublimation vs DTF printing →
          </Link>
        </p>
      </SEOPageLayout>
    </>
  );
}
